import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { FaPiggyBank, FaShieldAlt, FaCalculator, FaChartLine, FaUmbrella, FaDollarSign } from 'react-icons/fa';

const PersonalRetirementAccountPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Services', link: '/services' },
    { label: 'Personal Retirement Account' }
  ];

  const accountTypes = [
    {
      icon: <FaPiggyBank className="w-8 h-8 text-primary" />,
      title: "Traditional IRA",
      description: "Tax-deductible contributions with tax-deferred growth until retirement withdrawals."
    },
    {
      icon: <FaShieldAlt className="w-8 h-8 text-primary" />,
      title: "Roth IRA",
      description: "After-tax contributions with tax-free growth and qualified tax-free withdrawals in retirement."
    },
    {
      icon: <FaCalculator className="w-8 h-8 text-primary" />,
      title: "SEP-IRA",
      description: "Simplified Employee Pension plans for self-employed individuals and small business owners."
    },
    {
      icon: <FaChartLine className="w-8 h-8 text-primary" />,
      title: "SIMPLE IRA",
      description: "Savings Incentive Match Plan for small businesses with up to 100 employees."
    },
    {
      icon: <FaUmbrella className="w-8 h-8 text-primary" />,
      title: "Rollover IRA",
      description: "Transfer retirement funds from employer-sponsored plans to maintain tax advantages."
    },
    {
      icon: <FaDollarSign className="w-8 h-8 text-primary" />,
      title: "Self-Directed IRA",
      description: "Expanded investment options including real estate, precious metals, and private investments."
    }
  ];

  const benefits = [
    "Tax advantages through deductible contributions or tax-free growth",
    "Professional portfolio management and investment guidance",
    "Flexible contribution schedules to fit your budget",
    "Comprehensive retirement planning and goal setting",
    "Access to a wide range of investment options",
    "Regular account monitoring and performance reporting",
    "Estate planning integration for beneficiary protection",
    "Rollover assistance from employer-sponsored plans"
  ];

  const planningSteps = [
    {
      step: "01",
      title: "Assessment & Goal Setting",
      description: "Evaluate your current financial situation and define retirement objectives."
    },
    {
      step: "02",
      title: "Account Selection",
      description: "Choose the most suitable IRA type based on your income and tax situation."
    },
    {
      step: "03",
      title: "Investment Strategy",
      description: "Develop a diversified investment portfolio aligned with your risk tolerance."
    },
    {
      step: "04",
      title: "Ongoing Management",
      description: "Regular review and adjustment of your retirement strategy as circumstances change."
    }
  ];

  return (
    <>
      <PageHeader 
        title="Personal Retirement Account" 
        breadcrumbs={breadcrumbs}
        backgroundImage="/assets/finance-money-debt-credit-balance-concept_53876-127372.jpg_rkd2ew.jpg"
      />
      
      <section className="py-16 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-heading font-bold text-primary mb-6">
                Secure Your Financial Future
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                <span className="font-semibold">Blockfirst Capital</span> offers comprehensive Personal Retirement Account (IRA) 
                services designed to help you build and preserve wealth for your retirement years. Our expert 
                team provides personalized guidance to maximize your retirement savings while taking advantage 
                of available tax benefits.
              </p>
              <p className="text-lg text-gray-700">
                Whether you're just starting your career or approaching retirement, we offer flexible IRA 
                solutions with professional management to help ensure your retirement goals become reality.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/people-discussing-business-plan.jpg" 
                alt="Retirement Planning" 
                className="rounded-xl shadow-custom w-full h-full object-cover"
              />
            </motion.div>
          </div>

          {/* Account Types */}
          <div className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-heading font-bold text-primary mb-4">IRA Account Options</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Choose from various IRA types, each offering unique benefits and tax advantages to 
                optimize your retirement savings strategy.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {accountTypes.map((account, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-6 shadow-custom hover:shadow-custom-lg transition-all duration-300"
                >
                  <div className="bg-gray-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                    {account.icon}
                  </div>
                  <h3 className="text-xl font-heading font-semibold mb-4 text-primary">{account.title}</h3>
                  <p className="text-gray-600">{account.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Planning Process */}
          <div className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-heading font-bold text-primary mb-4">Our Planning Process</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                A systematic approach to retirement planning that ensures your IRA strategy aligns with your long-term goals.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {planningSteps.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className="bg-gray-900 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mb-6 mx-auto">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-heading font-semibold mb-4 text-primary">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Benefits Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-heading font-bold text-primary mb-6">Why Choose Our IRA Services?</h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start"
                  >
                                    <div className="bg-gray-100 rounded-full p-2 mr-4 mt-1">
                  <div className="w-2 h-2 bg-gray-700 rounded-full"></div>
                    </div>
                    <p className="text-gray-700 text-lg">{benefit}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/co-workers-discussing-business-project.jpg" 
                alt="Financial Consultation" 
                className="rounded-xl shadow-custom w-full h-full object-cover"
              />
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="bg-primary text-white rounded-2xl p-8 md:p-12"
          >
            <div className="text-center">
              <h2 className="text-3xl font-heading font-bold mb-6">Ready to Start Your Retirement Planning?</h2>
              <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
                Contact our retirement planning specialists to discuss how a Personal Retirement Account 
                can help secure your financial future with tax-advantaged savings.
              </p>
              <a 
                href="/contact-us" 
                className="btn btn-secondary text-lg py-4 px-8 shadow-xl hover:shadow-secondary/20 btn-3d inline-block"
              >
                Schedule a Consultation
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default PersonalRetirementAccountPage; 